import React, { useState, useEffect, useRef } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import StopIcon from '@material-ui/icons/Stop'
import FlipCameraAndroidIcon from '@material-ui/icons/FlipCameraAndroid'
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography
} from '@material-ui/core'
import {
  withOneplaceLibraryContext,
  IOneplaceLibraryContextProp
} from '../OneplaceLibraryProvider'
import { IPhoto } from '../../models/Checklist'
import { createVideoPhoto } from './utils/imageUtils'
import { CONFIG } from '../../config'

export interface IHtmlVideoRecorderProps extends IOneplaceLibraryContextProp {
  open: boolean
  maxFileSize?: number // in MB
  onVideoRecorded: (photo: IPhoto) => void
  onClose: () => void
}

export const HtmlVideoRecorder = withOneplaceLibraryContext(
  (props: IHtmlVideoRecorderProps) => {
    const [isRecording, setIsRecording] = useState(false)
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')
    const [availableCameras, setAvailableCameras] = useState(0)
    const [error, setError] = useState<string | null>(null)

    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const recorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const cancelledRef = useRef(false)

    useEffect(() => {
      if (props.open) {
        stopCamera() // clean up previous stream before starting new one
        startCamera()
        countCameras()
      } else {
        stopCamera()
      }
      return () => stopCamera()
    }, [props.open, facingMode])

    // Reattach the live stream when the <video> element remounts after an error is cleared
    useEffect(() => {
      if (!error && videoRef.current && streamRef.current) {
        videoRef.current.srcObject = streamRef.current
      }
    }, [error])

    const countCameras = async () => {
      try {
        const devices = navigator.mediaDevices && await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(d => d.kind === 'videoinput')
        setAvailableCameras(videoDevices.length)
      } catch {
        setAvailableCameras(0)
      }
    }

    const startCamera = async () => {
      try {
        setError(null)
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: true
        })
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err) {
        setError('Unable to access camera. Please check permissions.')
      }
    }

    const stopCamera = () => {
      cancelledRef.current = true
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
      setIsRecording(false)
    }

    const startRecording = () => {
      if (!streamRef.current) return

      setError(null)
      cancelledRef.current = false
      chunksRef.current = []
      const mimeType = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : 'video/webm'

      const recorder = new MediaRecorder(streamRef.current, { mimeType })

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = () => {
        if (cancelledRef.current) return
        const blob = new Blob(chunksRef.current, { type: mimeType })

        const maxSize = props.maxFileSize || CONFIG.videoMaxFileSize
        if (blob.size / 1024 / 1024 > maxSize) {
          setError(`Video exceeds maximum size of ${maxSize} MB. Please record a shorter video.`)
          return
        }

        const extension = mimeType.includes('webm') ? 'webm' : 'mp4'
        const fileName = `video_${Date.now()}.${extension}`

        props.onVideoRecorded(
          createVideoPhoto(blob, fileName, 'camera', mimeType.split(';')[0])
        )
      }

      recorderRef.current = recorder
      recorder.start()
      setIsRecording(true)
    }

    const stopRecording = () => {
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop()
      }
      setIsRecording(false)
    }

    const handleClose = () => {
      stopCamera()
      props.onClose()
    }

    const handleSwitchCamera = () => {
      setFacingMode(prev => prev === 'environment' ? 'user' : 'environment')
    }

    return (
      <Dialog
        open={props.open}
        aria-labelledby='video-recorder-dialog'
        fullWidth={false}
        maxWidth='md'
      >
        <AppBar>
          <Toolbar>
            <IconButton
              edge='start'
              color='inherit'
              onClick={handleClose}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
            <Typography variant='h6'>
              {isRecording ? 'Recording...' : 'Video Recorder'}
            </Typography>
            {availableCameras > 1 && !isRecording && (
              <div style={{ marginLeft: 'auto' }}>
                <IconButton
                  aria-label='switch camera'
                  color='inherit'
                  title='Switch Camera'
                  onClick={handleSwitchCamera}
                >
                  <FlipCameraAndroidIcon />
                </IconButton>
              </div>
            )}
          </Toolbar>
        </AppBar>

        <div style={{ paddingTop: 64, backgroundColor: '#000', textAlign: 'center' }}>
          {error ? (
            <Typography
              variant='body1'
              style={{ color: '#fff', padding: 24 }}
            >
              {error}
            </Typography>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{ width: '100%', maxHeight: '60vh' }}
            />
          )}

          <div style={{ padding: 16 }}>
            {!isRecording ? (
              <IconButton
                onClick={startRecording}
                aria-label='start recording'
                style={{ color: '#f44336', backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <FiberManualRecordIcon style={{ fontSize: 48 }} />
              </IconButton>
            ) : (
              <IconButton
                onClick={stopRecording}
                aria-label='stop recording'
                style={{ color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                <StopIcon style={{ fontSize: 48 }} />
              </IconButton>
            )}
            {!error && (
              <Typography
                variant='caption'
                style={{ color: '#fff', display: 'block', marginTop: 8, opacity: 0.8 }}
              >
                Max file size is {props.maxFileSize || CONFIG.videoMaxFileSize} MB (Please keep to under 30s)
              </Typography>
            )}
          </div>
        </div>
      </Dialog>
    )
  }
)
